import React, { useState, useCallback } from 'react';
import {
  Sparkles, ArrowLeft, ArrowRight, Wand2, Music, Clock,
  Zap, Key, Users, Search, Loader2, CheckCircle, X,
  ChevronDown, Target, Waves, Flame, Edit2, AlertTriangle, Info
} from 'lucide-react';
import './AIWizard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Genre options
const GENRES = [
  'House', 'Tech House', 'Deep House', 'Progressive House',
  'Techno', 'Melodic Techno', 'Hard Techno', 'Minimal',
  'Trance', 'Progressive Trance', 'Uplifting Trance', 'Psytrance',
  'Drum & Bass', 'Dubstep', 'Bass Music', 'UK Garage',
  'Disco', 'Nu-Disco', 'Afro House', 'Amapiano',
  'Electro', 'Breakbeat', 'Downtempo', 'Ambient'
];

// Set type options
const SET_TYPES = [
  { value: 'club', label: 'Club Night', icon: '🏢' },
  { value: 'festival', label: 'Festival', icon: '🎪' },
  { value: 'warmup', label: 'Warm-up Set', icon: '🌅' },
  { value: 'peak_time', label: 'Peak Time', icon: '🔥' },
  { value: 'closing', label: 'Closing Set', icon: '🌙' },
  { value: 'livestream', label: 'Livestream', icon: '📺' },
  { value: 'radio', label: 'Radio Show', icon: '📻' },
  { value: 'bedroom', label: 'Bedroom / Practice', icon: '🎧' }
];

// Energy curve options
const ENERGY_CURVES = [
  { value: 'build', label: 'Build Up', desc: 'Gradually increase energy to peak', icon: <Target size={20} /> },
  { value: 'peak_time', label: 'Peak Time', desc: 'High energy throughout', icon: <Flame size={20} /> },
  { value: 'journey', label: 'Journey', desc: 'Waves of energy, dynamic flow', icon: <Waves size={20} /> },
  { value: 'warmup', label: 'Warm Up', desc: 'Slow build for opening', icon: <Zap size={20} /> },
  { value: 'closing', label: 'Wind Down', desc: 'Gradual cooldown', icon: <Music size={20} /> },
  { value: 'steady', label: 'Steady', desc: 'Consistent energy level', icon: <Target size={20} /> }
];

// BPM presets by genre
const BPM_PRESETS = {
  'House': { min: 120, max: 128 },
  'Tech House': { min: 124, max: 130 },
  'Deep House': { min: 118, max: 124 },
  'Progressive House': { min: 122, max: 128 },
  'Techno': { min: 128, max: 138 },
  'Melodic Techno': { min: 124, max: 132 },
  'Hard Techno': { min: 140, max: 155 },
  'Minimal': { min: 124, max: 132 },
  'Trance': { min: 138, max: 145 },
  'Progressive Trance': { min: 132, max: 140 },
  'Uplifting Trance': { min: 138, max: 142 },
  'Psytrance': { min: 145, max: 150 },
  'Drum & Bass': { min: 170, max: 178 },
  'Dubstep': { min: 140, max: 150 },
  'Disco': { min: 110, max: 125 },
  'Afro House': { min: 118, max: 126 }
};

// Camelot keys
const CAMELOT_KEYS = [
  '1A', '2A', '3A', '4A', '5A', '6A', '7A', '8A', '9A', '10A', '11A', '12A',
  '1B', '2B', '3B', '4B', '5B', '6B', '7B', '8B', '9B', '10B', '11B', '12B'
];

const AIWizard = ({ onClose, onCrateCreated }) => {
  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [parsedVibe, setParsedVibe] = useState(null);
  const [error, setError] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    vibeDescription: '',
    crateType: 'mock', // mock or live
    genre: '',
    setType: '',
    setLength: 60,
    bpmMin: 124,
    bpmMax: 130,
    energyCurve: 'journey',
    startingKey: '',
    referenceArtists: '',
    sources: ['spotify', 'discogs']
  });

  const getAuthToken = useCallback(() => localStorage.getItem('beatseek_token'), []);

  // Update BPM when genre changes
  const handleGenreChange = (genre) => {
    setFormData(prev => {
      const preset = BPM_PRESETS[genre] || { min: 124, max: 130 };
      return {
        ...prev,
        genre,
        bpmMin: preset.min,
        bpmMax: preset.max
      };
    });
  };

  // Parse vibe description with AI
  const parseVibe = async () => {
    if (!formData.vibeDescription.trim()) return;
    
    setIsParsing(true);
    setError(null);
    
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/ai/parse-vibe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          vibe_description: formData.vibeDescription
        })
      });

      if (!response.ok) {
        throw new Error('Failed to parse vibe');
      }

      const data = await response.json();
      setParsedVibe(data.parsed);
      
      // Auto-fill some fields from parsed data
      if (data.parsed) {
        const p = data.parsed;
        setFormData(prev => ({
          ...prev,
          genre: prev.genre || (p.suggested_genres?.[0] || ''),
          bpmMin: p.bpm_range?.min || prev.bpmMin,
          bpmMax: p.bpm_range?.max || prev.bpmMax,
          energyCurve: p.energy_curve || prev.energyCurve,
          referenceArtists: prev.referenceArtists || (p.reference_artists?.slice(0, 5).join(', ') || '')
        }));
      }
    } catch (err) {
      console.error('Error parsing vibe:', err);
      setError('Failed to analyze your vibe. You can still continue manually.');
    } finally {
      setIsParsing(false);
    }
  };

  // Generate the crate
  const generateCrate = async () => {
    if (!formData.name.trim() || !formData.vibeDescription.trim()) {
      setError('Please fill in the crate name and vibe description');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/ai/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          type: formData.crateType,
          vibe_description: formData.vibeDescription,
          genre: formData.genre || null,
          set_type: formData.setType || null,
          set_length_minutes: formData.setLength,
          bpm_range: { min: formData.bpmMin, max: formData.bpmMax },
          energy_curve: formData.energyCurve,
          starting_key: formData.startingKey || null,
          reference_artists: formData.referenceArtists.split(',').map(a => a.trim()).filter(Boolean),
          sources: formData.sources
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to generate crate');
      }

      const data = await response.json();
      
      if (data.success && data.crate) {
        onCrateCreated?.(data.crate);
        onClose();
      }
    } catch (err) {
      console.error('Error generating crate:', err);
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Navigation
  const canGoNext = () => {
    switch (step) {
      case 1:
        return formData.name.trim() && formData.vibeDescription.trim();
      case 2:
        return true; // Optional fields
      case 3:
        return true; // Optional fields
      default:
        return true;
    }
  };

  const goNext = () => {
    if (step === 1 && !parsedVibe && formData.vibeDescription.trim()) {
      parseVibe();
    }
    if (step < 4) setStep(step + 1);
  };

  const goBack = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="ai-wizard-overlay" onClick={onClose}>
      <div className="ai-wizard" onClick={(e) => e.stopPropagation()} data-testid="ai-wizard">
        {/* Header */}
        <div className="wizard-header">
          <div className="wizard-title">
            <Sparkles size={24} className="sparkle-icon" />
            <h2>AI Set Builder</h2>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Progress */}
        <div className="wizard-progress">
          {[1, 2, 3, 4].map(s => (
            <div 
              key={s} 
              className={`progress-step ${step >= s ? 'active' : ''} ${step === s ? 'current' : ''}`}
            >
              <div className="step-dot">
                {step > s ? <CheckCircle size={16} /> : s}
              </div>
              <span className="step-label">
                {s === 1 && 'Vibe'}
                {s === 2 && 'Style'}
                {s === 3 && 'Details'}
                {s === 4 && 'Generate'}
              </span>
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="wizard-content">
          {/* Step 1: Vibe Description */}
          {step === 1 && (
            <div className="wizard-step" data-testid="wizard-step-1">
              <h3>Describe Your Vibe</h3>
              <p className="step-description">
                Tell us about the mood, energy, and atmosphere you want for your set
              </p>

              <div className="form-group">
                <label>Crate Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Saturday Night Warehouse"
                  data-testid="crate-name-input"
                />
              </div>

              <div className="form-group">
                <label>Crate Type</label>
                <div className="ai-type-selector">
                  <button
                    type="button"
                    className={`ai-type-option ${formData.crateType === 'mock' ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, crateType: 'mock' }))}
                    data-testid="ai-type-mock-btn"
                  >
                    <div 
                      className="ai-type-info-btn"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Info size={14} />
                      <div className="ai-type-info-tooltip">
                        <p>Mock Set: Perfect for planning future sets. Add any track regardless of ownership. Great for wish lists and set ideas.</p>
                        <p className="tooltip-warning"><AlertTriangle size={12} /> Not available for download.</p>
                      </div>
                    </div>
                    <Edit2 size={20} />
                    <span className="ai-type-label">Mock</span>
                    <span className="ai-type-desc">For planning - any tracks</span>
                  </button>
                  <button
                    type="button"
                    className={`ai-type-option ${formData.crateType === 'live' ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, crateType: 'live' }))}
                    data-testid="ai-type-live-btn"
                  >
                    <div 
                      className="ai-type-info-btn"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Info size={14} />
                      <div className="ai-type-info-tooltip">
                        <p>Live Set: For gig-ready sets. Only allows tracks you own or have verified access to. Export directly to DJ software.</p>
                      </div>
                    </div>
                    <CheckCircle size={20} />
                    <span className="ai-type-label">Live</span>
                    <span className="ai-type-desc">Only verified tracks</span>
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Vibe Description *</label>
                <textarea
                  value={formData.vibeDescription}
                  onChange={(e) => setFormData(prev => ({ ...prev, vibeDescription: e.target.value }))}
                  placeholder="e.g., Dark driving techno for a late night warehouse party, hypnotic and relentless with industrial undertones..."
                  rows={4}
                  data-testid="vibe-input"
                />
                <small>Be as descriptive as you want - our AI will extract the key parameters</small>
              </div>

              {isParsing && (
                <div className="parsing-indicator">
                  <Loader2 size={20} className="spinning" />
                  Analyzing your vibe...
                </div>
              )}

              {parsedVibe && (
                <div className="parsed-result">
                  <div className="parsed-header">
                    <Sparkles size={16} />
                    AI Analysis
                  </div>
                  <div className="parsed-tags">
                    {parsedVibe.suggested_genres?.map(g => (
                      <span key={g} className="tag genre">{g}</span>
                    ))}
                    {parsedVibe.parsed_vibe?.mood?.slice(0, 3).map(m => (
                      <span key={m} className="tag mood">{m}</span>
                    ))}
                    {parsedVibe.bpm_range && (
                      <span className="tag bpm">{parsedVibe.bpm_range.min}-{parsedVibe.bpm_range.max} BPM</span>
                    )}
                  </div>
                  {parsedVibe.reference_artists?.length > 0 && (
                    <div className="parsed-artists">
                      <Users size={14} />
                      Similar to: {parsedVibe.reference_artists.slice(0, 5).join(', ')}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Style & Genre */}
          {step === 2 && (
            <div className="wizard-step" data-testid="wizard-step-2">
              <h3>Style & Genre</h3>
              <p className="step-description">
                Fine-tune the genre and set type (we&apos;ve suggested some based on your vibe)
              </p>

              <div className="form-group">
                <label>Genre</label>
                <select
                  value={formData.genre}
                  onChange={(e) => handleGenreChange(e.target.value)}
                  data-testid="genre-select"
                >
                  <option value="">Select genre...</option>
                  {GENRES.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Set Type</label>
                <div className="set-type-grid">
                  {SET_TYPES.map(st => (
                    <button
                      key={st.value}
                      type="button"
                      className={`set-type-btn ${formData.setType === st.value ? 'active' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, setType: st.value }))}
                    >
                      <span className="set-type-icon">{st.icon}</span>
                      <span className="set-type-label">{st.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Set Length: {formData.setLength} minutes</label>
                <input
                  type="range"
                  min="30"
                  max="180"
                  step="15"
                  value={formData.setLength}
                  onChange={(e) => setFormData(prev => ({ ...prev, setLength: parseInt(e.target.value) }))}
                  className="range-slider"
                />
                <div className="range-labels">
                  <span>30 min</span>
                  <span>1 hr</span>
                  <span>2 hr</span>
                  <span>3 hr</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Technical Details */}
          {step === 3 && (
            <div className="wizard-step" data-testid="wizard-step-3">
              <h3>Technical Details</h3>
              <p className="step-description">
                Set your BPM range, energy curve, and other parameters
              </p>

              <div className="form-row">
                <div className="form-group">
                  <label>BPM Range</label>
                  <div className="bpm-inputs">
                    <input
                      type="number"
                      value={formData.bpmMin}
                      onChange={(e) => setFormData(prev => ({ ...prev, bpmMin: parseInt(e.target.value) || 100 }))}
                      min="60"
                      max="200"
                      data-testid="bpm-min"
                    />
                    <span>to</span>
                    <input
                      type="number"
                      value={formData.bpmMax}
                      onChange={(e) => setFormData(prev => ({ ...prev, bpmMax: parseInt(e.target.value) || 140 }))}
                      min="60"
                      max="200"
                      data-testid="bpm-max"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Starting Key (Optional)</label>
                  <select
                    value={formData.startingKey}
                    onChange={(e) => setFormData(prev => ({ ...prev, startingKey: e.target.value }))}
                  >
                    <option value="">Any key</option>
                    {CAMELOT_KEYS.map(k => (
                      <option key={k} value={k}>{k}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Energy Curve</label>
                <div className="energy-curve-grid">
                  {ENERGY_CURVES.map(ec => (
                    <button
                      key={ec.value}
                      type="button"
                      className={`energy-curve-btn ${formData.energyCurve === ec.value ? 'active' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, energyCurve: ec.value }))}
                    >
                      {ec.icon}
                      <span className="ec-label">{ec.label}</span>
                      <span className="ec-desc">{ec.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Reference Artists (comma separated)</label>
                <input
                  type="text"
                  value={formData.referenceArtists}
                  onChange={(e) => setFormData(prev => ({ ...prev, referenceArtists: e.target.value }))}
                  placeholder="e.g., Amelie Lens, Charlotte de Witte, SNTS"
                />
              </div>
            </div>
          )}

          {/* Step 4: Review & Generate */}
          {step === 4 && (
            <div className="wizard-step" data-testid="wizard-step-4">
              <h3>Review & Generate</h3>
              <p className="step-description">
                Review your settings and generate your AI-curated crate
              </p>

              <div className="review-summary">
                <div className="review-item">
                  <span className="label">Crate Name</span>
                  <span className="value">{formData.name}</span>
                </div>
                <div className="review-item">
                  <span className="label">Crate Type</span>
                  <span className={`value crate-type-badge ${formData.crateType}`}>
                    {formData.crateType === 'mock' ? 'Mock Set' : 'Live Set'}
                  </span>
                </div>
                <div className="review-item">
                  <span className="label">Vibe</span>
                  <span className="value vibe">{formData.vibeDescription.slice(0, 100)}...</span>
                </div>
                <div className="review-item">
                  <span className="label">Genre</span>
                  <span className="value">{formData.genre || 'Auto-detect'}</span>
                </div>
                <div className="review-item">
                  <span className="label">Set Type</span>
                  <span className="value">{SET_TYPES.find(s => s.value === formData.setType)?.label || 'Not specified'}</span>
                </div>
                <div className="review-item">
                  <span className="label">Duration</span>
                  <span className="value">{formData.setLength} minutes</span>
                </div>
                <div className="review-item">
                  <span className="label">BPM Range</span>
                  <span className="value">{formData.bpmMin} - {formData.bpmMax}</span>
                </div>
                <div className="review-item">
                  <span className="label">Energy Curve</span>
                  <span className="value">{ENERGY_CURVES.find(e => e.value === formData.energyCurve)?.label}</span>
                </div>
                {formData.referenceArtists && (
                  <div className="review-item">
                    <span className="label">Reference Artists</span>
                    <span className="value">{formData.referenceArtists}</span>
                  </div>
                )}
              </div>

              <div className="generate-info">
                <Wand2 size={20} />
                <p>
                  Your crate will be created with these parameters. 
                  You can then search and add tracks from Diggin&apos; in the Crates, 
                  and the AI will help you order them harmonically.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="wizard-error">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="wizard-footer">
          <button 
            className="back-btn"
            onClick={step === 1 ? onClose : goBack}
          >
            {step === 1 ? 'Cancel' : <><ArrowLeft size={18} /> Back</>}
          </button>

          {step < 4 ? (
            <button 
              className="next-btn"
              onClick={goNext}
              disabled={!canGoNext() || isParsing}
            >
              {isParsing ? (
                <><Loader2 size={18} className="spinning" /> Analyzing...</>
              ) : (
                <>Next <ArrowRight size={18} /></>
              )}
            </button>
          ) : (
            <button 
              className="generate-btn"
              onClick={generateCrate}
              disabled={isGenerating}
              data-testid="generate-crate-btn"
            >
              {isGenerating ? (
                <><Loader2 size={18} className="spinning" /> Generating...</>
              ) : (
                <><Sparkles size={18} /> Generate Crate</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIWizard;
